import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { NgIf } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PageEvent, MatPaginatorModule } from '@angular/material/paginator';
import { StateNotifyService } from '../../../../common/state/service/state-notify.service';
import { FileService } from '../../../../common/file/service/file.service';
import { TestStepPictureService } from '../../service/test-step-picture.service';
import { TestStepPicture } from '../../dto/test-step-picture';
import { TestStep } from '../../dto/test-step';

@Component({
  selector: 'dpi-test-step-picture',
  imports: [MatCardModule, MatIconModule, MatButtonModule, MatToolbarModule, MatTooltipModule, MatPaginatorModule, NgIf],
  templateUrl: './test-step-picture.component.html',
  styleUrl: './test-step-picture.component.scss'
})
export class TestStepPictureComponent implements OnInit, OnDestroy {
  testStep: TestStep;
  listDiagram: TestStepPicture[] = [];
  imgFromApi: string = '';
  destroyed$ = new Subject<void>(); // notifier of component destroyed
  @Output('listSizeChanged') listSize = new EventEmitter<number>();
  isReadonly: boolean;

  pageLength = 0;
  pageSize = 1;
  pageIndex = 0;
  pageSizeOptions = [1];
  hidePageSize = true;
  showPageSizeOptions = false;
  showFirstLastButtons = true;
  disabled = false;
  pageEvent: PageEvent | undefined;
  handlePageEvent(e: PageEvent) {
    this.pageEvent = e;
    this.pageLength = e.length;
    this.pageSize = e.pageSize;
    this.pageIndex = e.pageIndex;
    this.reloadImages();
  }
  
  // 配置项
  allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf']; // 允许的文件类型
  maxSizeMB = 100; // 最大 100MB

  // 状态
  selectedFile: File | null = null;
  isDragging = false;
  uploadError = '';

  constructor(private notifyService: StateNotifyService, private testStepPictureService: TestStepPictureService, private fileService: FileService) {
    this.testStep = this.notifyService.getSelectedTestStep();
    this.isReadonly = true;
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  ngOnInit(): void {
    this.notifyService.testStepSelection$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(model => {
        this.testStep = model;
        this.loadListDiagram(model.id, true);
      })

    this.notifyService.readonlyMode$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(res => {
        this.isReadonly = res;
        this.resetStatus();
      });
  }

  loadListDiagram(testStepId: number, displayFromFirstOne: boolean) {
    this.testStepPictureService.getAllPicturesOfTestStep(testStepId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (res: any) => {
          this.listDiagram = res;
          this.listSize.emit(this.listDiagram.length);
          if (displayFromFirstOne) {
            this.pageIndex = 0;
          } else {
            this.pageIndex = this.listDiagram.length - 1;
          }
          this.reloadImages();
        },
        error: (error: any) => {
          console.error('Error: ', error);
          this.listDiagram = [];
        }
      });
  }

  reloadImages() {
    this.pageLength = this.listDiagram.length;
    if (this.pageLength > 0) {
      const diagramRecord = this.listDiagram[this.pageIndex];
      this.testStepPictureService.getTestStepPicture(diagramRecord.id)
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: (img: any) => {
            this.imgFromApi = this.fileService.arrayBufferToBase64(img);
          },
          error: (error: any) => {
            console.error('Error: ', error);
            this.imgFromApi = '';
          }
        });
    } else {
      this.imgFromApi = '';
    }
  }

  onFileSelected(event: any) {
    const file: File = event.target.files[0];
    if (file) {
      //this.fileName = file.name;
      this.testStepPictureService.fileUpload(this.testStep.id, file).subscribe({
        next: res => {
          this.loadListDiagram(this.testStep.id, false);
        },
        error: err => {
          console.log(err);
        }
      });
    }
  }

  deleteTestStepPicture() {
    if (this.pageLength > 0) {
      const diagramRecord = this.listDiagram[this.pageIndex];
      this.testStepPictureService.deleteTestStepPicture(diagramRecord.id)
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: (res: any) => {
            this.loadListDiagram(this.testStep.id, true);
          },
          error: (error: any) => {
            console.error('Error: ', error);
          }
        });
    }
  }

  // ------------------------------
  // 拖放事件
  // ------------------------------
  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragging = true;
  }

  onDragLeave() {
    this.isDragging = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragging = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.validateAndSetFile(files[0]);
    }
  }

  // ------------------------------
  // 文件验证与上传
  // ------------------------------
  validateAndSetFile(file: File) {
    this.resetStatus();

    // 检查类型
    if (!this.allowedTypes.includes(file.type)) {
      this.uploadError = '不支持此文件类型';
      return;
    }

    // 检查大小
    const maxSize = this.maxSizeMB * 1024 * 1024;
    if (file.size > maxSize) {
      this.uploadError = `文件不能超过 ${this.maxSizeMB}MB`;
      return;
    }

    this.selectedFile = file;

    if (file) {
      this.testStepPictureService.fileUpload(this.testStep.id, file).subscribe({
        next: res => {
          this.loadListDiagram(this.testStep.id, false);
        },
        error: err => {
          console.log(err);
        }
      });
    }

  }

  // 重置
  resetStatus() {
    this.selectedFile = null;
    this.uploadError = '';
  }

}
